import {
  ButtonGroup,
  Form,
  FormButton,
  FormSubmitHandler,
  showNotification,
  Tabs,
} from 'platform/components';
import {Box, Hide, Space} from 'platform/foundation';
import {DeepPartial} from 'utility-types';

import {useSelector} from 'react-redux';

import {equals} from 'ramda';

import {selectActiveBranchId} from '@dms/api/features';
import {
  GetArticleUpdateJobResponse,
  PatchArticleUpdateJobRequest,
  PostArticleUpdateJobRequest,
  usePatchArticleUpdateJobMutation,
  usePostArticleUpdateJobMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {handleApiError, queryParams, Section, usePermissions} from '@dms/shared';

import {
  composePath,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
  useQueryState,
} from 'shared/web';

import {ApprovalExecutionDetailsTab} from './components/ApprovalExecutionDetailsTab';
import {GeneralInformationTab} from './components/GeneralInformationTab';
import {DRAFT_DEFAULT_VALUES} from './constants/draftDefaultValues';
import {OverviewForm, OverviewFormSchema} from './types/OverviewForm';

interface OverviewProps extends RequiredTestIdProps {
  isDraft: boolean;
  articleUpdateJob: GetArticleUpdateJobResponse | Nullish;
}

export function Overview(props: OverviewProps) {
  const navigate = useNavigate();

  const [articleUpdateJobEdit] = usePermissions({
    permissionKeys: ['articleUpdateJobEdit'],
  });

  const branchId = useSelector(selectActiveBranchId);

  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const [postArticleUpdateJob, {isLoading: isCreating}] = usePostArticleUpdateJobMutation();

  const [patchArticleUpdateJob, {isLoading: isSavingChanges}] = usePatchArticleUpdateJobMutation();

  const source = props.articleUpdateJob?.source;

  const defaultValues: DeepPartial<OverviewForm> = props.isDraft
    ? DRAFT_DEFAULT_VALUES
    : {
        basicInformation: {
          supplierId: props.articleUpdateJob?.supplierId,
          publishedAt: props.articleUpdateJob?.publishedAt,
          note: props.articleUpdateJob?.note,
        },
        articlesScope: {
          articleScopeLimitToUpdateList: props.articleUpdateJob?.articleScopeLimitToUpdateList,
          branchScope: {
            articleScopeBranchAll: props.articleUpdateJob?.articleScopeBranchAll,
            articleScopeBranches: props.articleUpdateJob?.articleScopeBranches,
            option: props.articleUpdateJob?.articleScopeBranchAll ? 'all' : 'select',
          },
          warehouseScope: {
            articleScopeWarehouseAll: props.articleUpdateJob?.articleScopeWarehouseAll,
            articleScopeWarehouses: props.articleUpdateJob?.articleScopeWarehouses,
            option: props.articleUpdateJob?.articleScopeWarehouseAll ? 'all' : 'select',
          },
          articleScopeArticleDefaultSupplier:
            props.articleUpdateJob?.articleScopeArticleDefaultSupplier,
        },
        updateScope: {
          updateScopeSupplierArticleAll: props.articleUpdateJob?.updateScopeSupplierArticleAll,
          updateScopeSupplierArticle: props.articleUpdateJob?.updateScopeSupplierArticle,
          updateScopeWarehouseArticleAll: props.articleUpdateJob?.updateScopeWarehouseArticleAll,
          updateScopeWarehouseArticle: props.articleUpdateJob?.updateScopeWarehouseArticle,
        },
      };

  const navigateToDetail = (id: string) => {
    navigate(
      composePath(warehouseRoutes.updateJobDetailOverview, {
        params: {id},
      })
    );
  };

  const navigateToList = () => {
    navigate(warehouseRoutes.updateJobsList);
  };

  const handleSubmit: FormSubmitHandler<OverviewForm> = async (formValues) => {
    if (equals(formValues, defaultValues)) {
      return;
    }

    if (props.isDraft) {
      const request: PostArticleUpdateJobRequest = {
        branchId,
        source: 'USER',
        supplierId: formValues.basicInformation.supplierId,
        note: formValues.basicInformation?.note,
        articleScopeLimitToUpdateList: formValues.articlesScope.articleScopeLimitToUpdateList,
        articleScopeBranchAll: formValues.articlesScope.branchScope.articleScopeBranchAll,
        articleScopeBranches: formValues.articlesScope.branchScope?.articleScopeBranches,
        articleScopeWarehouseAll: formValues.articlesScope.warehouseScope.articleScopeWarehouseAll,
        articleScopeWarehouses: formValues.articlesScope.warehouseScope?.articleScopeWarehouses,
        articleScopeArticleDefaultSupplier:
          formValues.articlesScope.articleScopeArticleDefaultSupplier,
        updateScopeSupplierArticleAll: formValues.updateScope.updateScopeSupplierArticleAll,
        updateScopeSupplierArticle: formValues.updateScope.updateScopeSupplierArticle,
        updateScopeWarehouseArticleAll: formValues.updateScope.updateScopeWarehouseArticleAll,
        updateScopeWarehouseArticle: formValues.updateScope?.updateScopeWarehouseArticle,
      };

      return await postArticleUpdateJob(request)
        .unwrap()
        .then((result) => {
          showNotification.success(i18n.t('general.notifications.successfullyCreated'));
          navigateToDetail(result.articleUpdateJobId);
        })
        .catch(handleApiError);
    }

    const requestBody: PatchArticleUpdateJobRequest['body'] = {
      note: formValues.basicInformation.note,
      supplierId: formValues.basicInformation.supplierId,
      articleScopeLimitToUpdateList: formValues.articlesScope.articleScopeLimitToUpdateList,
      articleScopeBranchAll: formValues.articlesScope.branchScope.articleScopeBranchAll,
      articleScopeBranches: formValues.articlesScope.branchScope.articleScopeBranches,
      articleScopeWarehouseAll: formValues.articlesScope.warehouseScope.articleScopeWarehouseAll,
      articleScopeWarehouses: formValues.articlesScope.warehouseScope.articleScopeWarehouses,
      articleScopeArticleDefaultSupplier:
        formValues.articlesScope.articleScopeArticleDefaultSupplier,
      updateScopeSupplierArticleAll: formValues.updateScope.updateScopeSupplierArticleAll,
      updateScopeSupplierArticle: formValues.updateScope.updateScopeSupplierArticle,
      updateScopeWarehouseArticleAll: formValues.updateScope.updateScopeWarehouseArticleAll,
      updateScopeWarehouseArticle: formValues.updateScope.updateScopeWarehouseArticle,
    };

    await patchArticleUpdateJob({
      articleUpdateJobId: props.articleUpdateJob?.articleUpdateJobId as string,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  return (
    <Section isFullHeight>
      <Form<OverviewForm>
        isFullHeight
        defaultValues={defaultValues}
        experimentalZodSchema={OverviewFormSchema}
        onSubmit={handleSubmit}
        isDisabled={!articleUpdateJobEdit}
      >
        {(control, formApi) => (
          <Box height="100%">
            <Box height="95%">
              <Tabs
                isFullHeight
                isLazy
                variant="condensed"
                activeTabId={activeTabId}
                onChange={setActiveTabId}
                tabs={[
                  {
                    id: 'general-information',
                    title: i18n.t('entity.warehouse.labels.generalInformation'),
                    content: (
                      <GeneralInformationTab
                        control={control}
                        formApi={formApi}
                        source={source}
                        data-testid={suffixTestId('generalInformation', props)}
                      />
                    ),
                  },
                  {
                    id: 'approval-execution-details',
                    title: i18n.t('entity.warehouse.labels.approvalExecutionDetails'),
                    content: (
                      <ApprovalExecutionDetailsTab
                        articleUpdateJob={props.articleUpdateJob}
                        data-testid={suffixTestId('approvalExecutionDetails', props)}
                      />
                    ),
                  },
                ]}
              />
              <Space vertical={2} />
              <ButtonGroup align="right">
                <Hide when={props.isDraft}>
                  <FormButton
                    isLoading={isCreating || isSavingChanges}
                    control={control}
                    type="button"
                    variant="secondary"
                    title={i18n.t('general.actions.discardChanges')}
                    onClick={() => formApi.reset(defaultValues)}
                    data-testid={suffixTestId('actions.discard', props)}
                  />
                </Hide>

                <FormButton
                  isLoading={isCreating || isSavingChanges}
                  control={control}
                  type="button"
                  variant="secondary"
                  title={i18n.t('general.labels.backToUpdateJobs')}
                  onClick={navigateToList}
                  data-testid={suffixTestId('actions.backToUpdateJobs', props)}
                />
                <FormButton
                  isLoading={isCreating || isSavingChanges}
                  control={control}
                  type="submit"
                  title={
                    props.isDraft
                      ? i18n.t('general.actions.save')
                      : i18n.t('general.actions.saveChanges')
                  }
                  data-testid={suffixTestId('actions.submit', props)}
                />
              </ButtonGroup>
            </Box>
          </Box>
        )}
      </Form>
    </Section>
  );
}
